import { StudentSession } from "@axl/membership-sdk";
import { Alert, Button, Space, Statistic } from "antd";
import moment from "moment";

import {
  useAuth,
  useLoc,
  useSdk,
  useTokenStorage,
} from "@app/shared/lib/hooks";


import styles from "./AdminMark.module.scss";

type Props = {
  session: StudentSession;
  /**
   * `item` — a plate inside the header row (desktop).
   * `banner` — a full-width strip rendered UNDER the header (phones and tablets): the plate is two
   * lines of text that squeezed the header's own controls and pushed the page down.
   */
  variant?: "item" | "banner";
};

export const AdminMark: React.FC<Props> = ({ session, variant = "item" }) => {
  const loc = useLoc();
  const { logout } = useAuth();
  const sdk = useSdk();
  const tokenStorage = useTokenStorage();

  const title = (
    <span className="fw-500">
      {loc.word("You are logged in as an student", {
        default: "You are logged in as an student",
      })}
    </span>
  );

  const countdown = session.expiredDate != null && (
    <Space size="small">
      {loc.word("Until the end of the session left")}
      <Statistic.Countdown
        valueStyle={{ fontSize: 14 }}
        value={moment(session.expiredDate!).valueOf()}
        format="HH:mm:ss"
        onFinish={() => logout()}
      />
      <Button
        size="small"
        type="link"
        className="p-0"
        onClick={async () => {
          if (!tokenStorage.refreshToken) return;
          const result = await sdk.api.auth.prolongSession(
            tokenStorage.refreshToken,
          );
          if (!result.success) logout();
        }}
      >
        {loc.word("Update", { default: "Update", case: "lower" })}
      </Button>
    </Space>
  );

  /* antd's warning Alert, not a hand-painted yellow box: it takes its fill AND its ink from the app
     theme, so the plate stays readable on the dark theme and under a school's dark header colour
     (the header re-inks everything inside it, which left light text on the light-yellow plate). */
  if (variant === "banner")
    return (
      <Alert
        banner
        type="warning"
        showIcon={false}
        className={styles.banner}
        message={
          <div className={styles.bannerContent}>
            {title}
            {countdown}
          </div>
        }
      />
    );

  return (
    <div className="flex align-center h-100 p-10">
      <Alert
        type="warning"
        showIcon={false}
        className={styles.container}
        message={
          <div className="flex flex-col lh-15">
            {title}
            {countdown}
          </div>
        }
      />
    </div>
  );
};
